<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex items-start justify-between mb-6">
      <div>
        <h2 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">
          {{ page.title }}
        </h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">
          /{{ page.slug }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button @click="handleExtract" :disabled="extracting" class="btn btn-secondary btn-sm">
          {{ extracting ? 'Extracting...' : (page.extracted ? 'Re-extract' : 'Extract Content') }}
        </button>
        <button @click="$emit('edit', page)" class="btn btn-secondary btn-sm">
          Edit
        </button>
        <button
          v-if="page?.content_optimization_status === 'deliverable_accepted'"
          @click="$emit('open-publish-panel', page)"
          :disabled="!page?.has_wp_connection"
          :title="!page?.has_wp_connection ? 'No WordPress connection active' : 'Publish optimized content to WordPress'"
          class="rounded-lg bg-green-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Publish to WP
        </button>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="relative border-b border-gray-200 dark:border-gray-700 mb-6">
      <!-- Left fade -->
      <div
        v-if="showLeftFade"
        class="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white dark:from-gray-800 to-transparent z-10 pointer-events-none" />
      <!-- Right fade -->
      <div
        v-if="showRightFade"
        class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white dark:from-gray-800 to-transparent z-10 pointer-events-none" />
      <nav
        ref="tabNav"
        class="flex gap-4 -mb-px overflow-x-auto scrollbar-hide"
        @scroll="updateScrollFades">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          :ref="el => { if (tab.key === activeTab) activeTabEl = el }"
          @click="!tab.locked && (activeTab = tab.key)"
          :class="[
            'pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0',
            activeTab === tab.key
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : tab.locked
                ? 'border-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 cursor-pointer'
          ]">
          {{ tab.label }}
          <span v-if="tab.locked" class="ml-1 text-xs">🔒</span>
        </button>
      </nav>
    </div>

    <!-- Per-step in-app docs -->
    <StepExplainer :step-key="activeTab" />

    <!-- Error -->
    <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4 mb-4">
      <p class="text-sm text-red-700 dark:text-red-400">
        {{ error }}
      </p>
    </div>

    <!-- Overview Tab -->
    <div v-if="activeTab === 'overview'">
      <div class="grid grid-cols-2 gap-4 mb-6">
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Page Type
          </dt>
          <dd class="text-sm text-gray-900 dark:text-gray-100 mt-1 capitalize">
            {{ formatPageType(page.page_type) }}
          </dd>
        </div>
        <div v-if="page.page_category && page.page_category !== 'uncategorized'">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Category
          </dt>
          <dd class="mt-1">
            <span class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
              {{ formatCategory(page.page_category) }}
            </span>
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Status
          </dt>
          <dd class="mt-1">
            <span :class="statusClass(page.status)" class="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium">{{ page.status }}</span>
          </dd>
        </div>
        <div v-if="page.meta_title">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Meta Title
          </dt>
          <dd class="text-sm text-gray-900 dark:text-gray-100 mt-1">
            {{ page.meta_title }}
          </dd>
        </div>
        <div v-if="page.meta_description">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Meta Description
          </dt>
          <dd class="text-sm text-gray-900 dark:text-gray-100 mt-1">
            {{ page.meta_description }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Extraction Status
          </dt>
          <dd class="text-sm mt-1">
            <span v-if="page.extracted" class="text-green-600 dark:text-green-400">
              Extracted ({{ formatContentLength(page.content_length) }} chars)
              <span v-if="page.last_extract_content_at" class="text-gray-400 text-xs ml-1">{{ formatDate(page.last_extract_content_at) }}</span>
            </span>
            <span v-else-if="page.last_failed_content_extract" class="text-red-500">
              Failed <span class="text-gray-400 text-xs ml-1">{{ formatDate(page.last_failed_content_extract) }}</span>
            </span>
            <span v-else class="text-gray-400">Not extracted</span>
          </dd>
        </div>
      </div>

      <div v-if="page.headings && page.headings.length > 0" class="mb-6">
        <h3 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-2">
          Headings
        </h3>
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-1">
          <div v-for="(heading, i) in page.headings" :key="i" :style="{ paddingLeft: `${(heading.level - 1) * 16}px` }" class="text-sm text-gray-700 dark:text-gray-300">
            <span class="text-xs text-gray-400 mr-1">H{{ heading.level }}</span>
            {{ heading.text }}
          </div>
        </div>
      </div>

      <div v-if="fullPage && fullPage.extracted_content && !showPasteForm" class="mb-6">
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100">
            Extracted Content
          </h3>
          <div class="flex gap-3">
            <button @click="openEditForm" class="text-xs text-primary-500 hover:text-primary-600 font-medium">
              Edit
            </button>
            <button @click="openPasteForm" class="text-xs text-primary-500 hover:text-primary-600 font-medium">
              Replace
            </button>
          </div>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 max-h-96 overflow-y-auto whitespace-pre-wrap leading-relaxed" v-html="fullPage.extracted_content" />
      </div>

      <!-- Edit/paste content form -->
      <div v-if="showPasteForm || (!fullPage?.extracted_content && !extracting)" class="mb-6">
        <h3 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-2">
          {{ editingContent ? 'Edit Content' : (fullPage?.extracted_content ? 'Replace Content' : 'Paste Page Content') }}
        </h3>
        <textarea
          v-model="pastedContent"
          rows="12"
          class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Paste or type the page content here..." />
        <div class="flex justify-end gap-2 mt-2">
          <button v-if="fullPage?.extracted_content" @click="showPasteForm = false; pastedContent = ''; editingContent = false" class="btn btn-secondary btn-sm">
            Cancel
          </button>
          <button @click="handleSaveContent" :disabled="!pastedContent.trim() || savingContent" class="btn btn-primary btn-sm">
            {{ savingContent ? 'Saving...' : 'Save Content' }}
          </button>
        </div>
      </div>

      <!-- Audit Section -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
        <PageAuditResults :page-id="page.id" @updated="$emit('updated')" />
      </div>

      <!-- Audit History -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
        <PageAuditHistory :page-id="page.id" />
      </div>
    </div>

    <!-- Analytics Tab -->
    <div v-if="activeTab === 'analytics'">
      <PageAnalytics
        :page-id="page.id"
        :ga-enabled="page.ga_enabled"
        :gsc-enabled="page.gsc_enabled"
        @updated="$emit('updated')" />
    </div>

    <!-- Keywords Tab -->
    <div v-else-if="activeTab === 'keywords'">
      <PageKeywordResearch
        :page-id="page.id"
        :current-selected-keyword="page.selected_keyword"
        @updated="handleKeywordUpdated" />
    </div>

    <!-- SERP Analysis Tab -->
    <div v-else-if="activeTab === 'serp'">
      <PageSerpAnalysis
        :page-id="page.id"
        :selected-keyword="localSelectedKeyword"
        :selected-candidate-id="page.selected_keyword_candidate_id"
        @confirmed="serpConfirmed = true" />
    </div>

    <!-- Competitors Tab -->
    <div v-else-if="activeTab === 'competitors'">
      <PageCompetitorAnalysis :page-id="page.id" @quality-profile-approved="qualityProfileApproved = true" />
    </div>

    <!-- Keyword Strategy Tab -->
    <div v-else-if="activeTab === 'strategy'">
      <PageKeywordStrategy :page-id="page.id" @approved="strategyApproved = true" />
    </div>

    <!-- Counting Tab -->
    <div v-else-if="activeTab === 'counting'">
      <PageKeywordCounting :page-id="page.id" @completed="countingCompleted = true" />
    </div>

    <!-- Topics Tab -->
    <div v-else-if="activeTab === 'topics'">
      <PageSemanticAnalysis :page-id="page.id" @approved="topicsApproved = true" />
    </div>

    <!-- Structure Tab -->
    <div v-else-if="activeTab === 'structure'">
      <PageHeadingMetaLock :page-id="page.id" @approved="structureApproved = true" />
    </div>

    <!-- Content Tab -->
    <div v-else-if="activeTab === 'content'">
      <PageContentOptimization :page-id="page.id" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { getWebsitePage, extractPageContent, updatePageContent } from '~/javascript/api/website_pages.js'
import { getSerpAnalysis } from '~/javascript/api/serp_analysis.js'
import { getCompetitorAnalysis } from '~/javascript/api/competitor_analysis.js'
import { getKeywordStrategy } from '~/javascript/api/keyword_strategy.js'
import { getKeywordOccurrences } from '~/javascript/api/keyword_occurrences.js'
import { getSemanticAnalysis } from '~/javascript/api/semantic_analysis.js'
import { getHeadingMetaLock } from '~/javascript/api/heading_meta_lock.js'
import PageKeywordResearch from './page_keyword_research.vue'
import PageSerpAnalysis from './page_serp_analysis.vue'
import PageCompetitorAnalysis from './page_competitor_analysis.vue'
import PageKeywordStrategy from './page_keyword_strategy.vue'
import PageKeywordCounting from './page_keyword_counting.vue'
import PageSemanticAnalysis from './page_semantic_analysis.vue'
import PageHeadingMetaLock from './page_heading_meta_lock.vue'
import PageContentOptimization from './page_content_optimization.vue'
import PageAuditResults from './page_audit_results.vue'
import PageAuditHistory from './page_audit_history.vue'
import PageAnalytics from './page_analytics.vue'
import StepExplainer from './step_explainer.vue'

const props = defineProps({
  page: { type: Object, required: true }
})

const emit = defineEmits(['on-close-panel', 'edit', 'updated', 'open-publish-panel'])

const fullPage = ref(null)
const extracting = ref(false)
const error = ref(null)
const activeTab = ref('overview')
const serpConfirmed = ref(false)
const qualityProfileApproved = ref(false)
const strategyApproved = ref(false)
const countingCompleted = ref(false)
const topicsApproved = ref(false)
const structureApproved = ref(false)
const localSelectedKeyword = ref(props.page.selected_keyword)
const showPasteForm = ref(false)
const pastedContent = ref('')
const savingContent = ref(false)
const editingContent = ref(false)
const tabNav = ref(null)
const activeTabEl = ref(null)
const showLeftFade = ref(false)
const showRightFade = ref(false)

const updateScrollFades = () => {
  const el = tabNav.value
  if (!el) return
  showLeftFade.value = el.scrollLeft > 4
  showRightFade.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
}

const scrollActiveTabIntoView = () => {
  nextTick(() => {
    if (activeTabEl.value && tabNav.value) {
      activeTabEl.value.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
      nextTick(updateScrollFades)
    }
  })
}

watch(activeTab, scrollActiveTabIntoView)

const tabs = computed(() => [
  { key: 'overview', label: 'Overview', locked: false },
  { key: 'analytics', label: 'Analytics', locked: false },
  { key: 'keywords', label: 'Keywords', locked: false },
  { key: 'serp', label: 'SERP Analysis', locked: !localSelectedKeyword.value },
  { key: 'competitors', label: 'Competitors', locked: !localSelectedKeyword.value || !serpConfirmed.value },
  { key: 'strategy', label: 'Keyword Strategy', locked: !qualityProfileApproved.value },
  { key: 'counting', label: 'Counting', locked: !strategyApproved.value },
  { key: 'topics', label: 'Topics', locked: !countingCompleted.value },
  { key: 'structure', label: 'Structure', locked: !topicsApproved.value },
  { key: 'content', label: 'Content', locked: !structureApproved.value }
])

const handleKeywordUpdated = async() => {
  emit('updated')
  // Re-fetch page to get updated selected_keyword
  try {
    const response = await getWebsitePage(props.page.id)
    if (response.data?.selected_keyword) {
      localSelectedKeyword.value = response.data.selected_keyword
    }
  } catch (_e) { /* ignore */ }
}

const fetchFullPage = async() => {
  try {
    const response = await getWebsitePage(props.page.id)
    fullPage.value = response.data
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching page detail:', e)
  }
}

const openPasteForm = () => {
  pastedContent.value = ''
  editingContent.value = false
  showPasteForm.value = true
}

const openEditForm = () => {
  pastedContent.value = fullPage.value?.extracted_content || ''
  editingContent.value = true
  showPasteForm.value = true
}

const handleSaveContent = async() => {
  if (!pastedContent.value.trim()) return
  try {
    savingContent.value = true
    error.value = null
    const response = await updatePageContent(props.page.id, pastedContent.value)
    fullPage.value = response.data
    showPasteForm.value = false
    pastedContent.value = ''
    editingContent.value = false
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to save content'
  } finally {
    savingContent.value = false
  }
}

const handleExtract = async() => {
  try {
    extracting.value = true
    error.value = null
    const response = await extractPageContent(props.page.id)
    fullPage.value = response.data
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || 'Extraction failed'
  } finally {
    extracting.value = false
  }
}

const formatCategory = (cat) => {
  if (!cat) return ''
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const formatPageType = (type) => {
  const map = { page: 'Page', location_page: 'Location Page', service_page: 'Service Page', service_location_page: 'Service + Location' }
  return map[type] || type
}

const statusClass = (status) => {
  const map = {
    draft: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    published: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
  }
  return map[status] || ''
}

const formatContentLength = (len) => {
  if (!len) return '0'
  if (len > 1000) return `${(len / 1000).toFixed(1)}k`
  return `${len}`
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString()
}

const checkSerpConfirmed = async() => {
  if (!props.page.selected_keyword) return
  try {
    const response = await getSerpAnalysis(props.page.id)
    if (response.data.serp_analysis?.confirmed) {
      serpConfirmed.value = true
    }
  } catch (e) {
    // No SERP analysis yet — that's fine
  }
}

const checkQualityProfileApproved = async() => {
  try {
    const response = await getCompetitorAnalysis(props.page.id)
    const serp = response.data.serp_analysis
    if (serp?.benchmarks && serp.benchmarks.length > 0) {
      qualityProfileApproved.value = true
    }
  } catch (e) {
    // No competitor analysis yet — that's fine
  }
}

const checkStrategyApproved = async() => {
  try {
    const response = await getKeywordStrategy(props.page.id)
    if (response.data.keyword_strategy?.approved) {
      strategyApproved.value = true
    }
  } catch (e) {
    // No strategy yet — that's fine
  }
}

const checkCountingCompleted = async() => {
  try {
    const response = await getKeywordOccurrences(props.page.id)
    if (response.data.keyword_occurrence?.status === 'completed') {
      countingCompleted.value = true
    }
  } catch (e) {
    // No counting yet — that's fine
  }
}

const checkTopicsApproved = async() => {
  try {
    const response = await getSemanticAnalysis(props.page.id)
    if (response.data.semantic_analysis?.status === 'completed' && response.data.semantic_analysis?.approved) {
      topicsApproved.value = true
    }
  } catch (e) {
    // No topics yet — that's fine
  }
}

const checkStructureApproved = async() => {
  try {
    const response = await getHeadingMetaLock(props.page.id)
    if (response.data.heading_meta_lock?.approved) {
      structureApproved.value = true
    }
  } catch (e) {
    // No structure lock yet — that's fine
  }
}

onMounted(() => {
  fetchFullPage()
  checkSerpConfirmed()
  checkQualityProfileApproved()
  checkStrategyApproved()
  checkCountingCompleted()
  checkTopicsApproved()
  checkStructureApproved()
  nextTick(updateScrollFades)
})
</script>
